import React, { useState } from 'react';
import { Box, Tab, Tabs, Paper } from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import PdfTemplatesPage from './PdfTemplatesPage';
import ProductBlockEditor from './ProductBlockEditor';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`pdf-designer-tabpanel-${index}`}
      aria-labelledby={`pdf-designer-tab-${index}`}
      style={{ height: '100%' }}
      {...other}
    >
      {value === index && <Box sx={{ height: '100%' }}>{children}</Box>}
    </div>
  );
}

/**
 * PdfDesignerPage - Main page for PDF template and block editing
 * 
 * Contains tabs for:
 * - Sidmallar (Page templates) - Full page PDF templates
 * - Produktblock (Product blocks) - Reusable mini-templates for products
 */
const PdfDesignerPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(180deg, #edf3fa 0%, #f7f9fc 100%)',
      }}
    >
      <Paper
        sx={{
          mx: 1,
          mt: 1,
          mb: 0.5,
          px: 1,
          py: 0.5,
          borderRadius: 2,
          border: '1px solid rgba(15, 23, 42, 0.08)',
          boxShadow: '0 8px 22px rgba(15, 23, 42, 0.06)',
          background: 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              minHeight: 42,
              p: 0.4,
              borderRadius: 2,
              backgroundColor: 'rgba(27, 57, 103, 0.06)',
              '& .MuiTabs-indicator': {
                display: 'none',
              },
              '& .MuiTab-root': {
                minHeight: 34,
                textTransform: 'none',
                color: '#35557f',
                fontWeight: 600,
                borderRadius: 1.5,
                mx: 0.25,
              },
              '& .MuiTab-root.Mui-selected': {
                backgroundColor: '#ffffff',
                color: '#1B3967',
                boxShadow: '0 1px 4px rgba(15, 23, 42, 0.12)',
              },
            }}
          >
            <Tab
              icon={<DescriptionIcon />}
              iconPosition="start"
              label="Sidmallar"
              id="pdf-designer-tab-0"
              aria-controls="pdf-designer-tabpanel-0"
            />
            <Tab
              icon={<ViewModuleIcon />}
              iconPosition="start"
              label="Produktblock"
              id="pdf-designer-tab-1"
              aria-controls="pdf-designer-tabpanel-1"
            />
          </Tabs>
        </Box>
      </Paper>

      {/* Tab content */}
      <Box sx={{ flexGrow: 1, minHeight: 0, overflow: 'hidden', px: 1, pb: 1 }}>
        <TabPanel value={activeTab} index={0}>
          <PdfTemplatesPage />
        </TabPanel>
        <TabPanel value={activeTab} index={1}>
          <ProductBlockEditor />
        </TabPanel>
      </Box>
    </Box>
  );
};

export default PdfDesignerPage;
